import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Play, 
  CheckCircle, 
  TrendingUp, 
  BarChart3, 
  ArrowLeft,
  RefreshCw,
  Clock,
  Users,
  Target,
  Globe,
  Filter,
  MapPin,
  Lock,
  LogOut
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAnalyticsSummary, getAnalyticsEvents, getAnalyticsByRegion, getAnalyticsAdminKey, setAnalyticsAdminKey, clearAnalyticsAdminKey } from '../services/api';

// Region colors for visual distinction
const REGION_COLORS = {
  'India': '#FF9933',
  'US & Canada': '#3B82F6',
  'Europe': '#8B5CF6',
  'SEA': '#10B981',
  'Middle East': '#F59E0B',
  'LATAM': '#EC4899',
  'Africa': '#EF4444',
  'Other': '#6B7280',
  'Unknown': '#9CA3AF'
};

// Simple bar chart component
const SimpleBarChart = ({ data, maxValue, color = 'blue' }) => {
  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 truncate max-w-[200px]" title={item.label}>
              {item.label}
            </span>
            <span className="font-medium text-gray-900">{item.value}</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min((item.value / maxValue) * 100, 100)}%`,
                backgroundColor: item.color || (color === 'blue' ? '#3B82F6' : 
                                 color === 'green' ? '#22C55E' : 
                                 color === 'orange' ? '#F97316' : '#3B82F6')
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Funnel chart component
const FunnelChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = data[0]?.count || 1;
  const colors = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#22C55E'];
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => {
        const width = Math.max((item.count / maxCount) * 100, 20);
        return (
          <div key={index} className="flex items-center gap-4">
            <div className="w-24 text-sm text-gray-600 text-right">
              {item.stage}
            </div>
            <div className="flex-1 relative">
              <div 
                className="h-10 rounded-lg flex items-center justify-center text-white font-medium transition-all duration-500"
                style={{ 
                  width: `${width}%`,
                  backgroundColor: colors[index] || colors[0],
                  minWidth: '80px'
                }}
              >
                {item.count.toLocaleString()}
              </div>
            </div>
            <div className="w-16 text-sm text-gray-500 text-right">
              {item.percentage.toFixed(1)}%
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Metric card component
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600'
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Region summary card
const RegionCard = ({ region, onClick, isSelected }) => {
  const color = REGION_COLORS[region.region] || '#6B7280';
  
  return (
    <div 
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      onClick={() => onClick(region.region)}
    >
      <div className="flex items-center gap-3 mb-2">
        <div 
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="font-medium text-gray-900">{region.region}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">Plays:</span>
          <span className="ml-1 font-medium">{region.summary?.total_plays || 0}</span>
        </div>
        <div>
          <span className="text-gray-500">Complete:</span>
          <span className="ml-1 font-medium">{region.summary?.total_completions || 0}</span>
        </div>
        <div className="col-span-2">
          <span className="text-gray-500">Rate:</span>
          <span className="ml-1 font-medium text-green-600">
            {region.summary?.completion_rate || 0}%
          </span>
        </div>
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAnalyticsAdminKey());
  const [authError, setAuthError] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [regionData, setRegionData] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAnalyticsAdminKey(keyInput);
    try {
      const res = await getAnalyticsSummary();
      if (res.success) {
        setIsAuthenticated(true);
      } else {
        throw new Error('Invalid');
      }
    } catch {
      clearAnalyticsAdminKey();
      setAuthError('Invalid admin key. Access denied.');
    }
  };

  const handleLogout = () => {
    clearAnalyticsAdminKey();
    setIsAuthenticated(false);
    setKeyInput('');
  };

  const fetchData = async () => {
    try {
      const [summaryRes, eventsRes, regionRes] = await Promise.all([
        getAnalyticsSummary(),
        getAnalyticsEvents({ limit: 100 }),
        getAnalyticsByRegion()
      ]);
      
      if (summaryRes.success) {
        setSummary(summaryRes);
      }
      
      if (eventsRes.success) {
        setEvents(eventsRes.events || []);
      }
      
      if (regionRes.success) {
        setRegionData(regionRes);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        clearAnalyticsAdminKey();
        setIsAuthenticated(false);
        return;
      }
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Analytics Dashboard</h2>
              <p className="text-sm text-gray-500 mt-1">Enter your admin key to access</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                data-testid="admin-key-input"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin API Key"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                autoFocus
              />
              {authError && (
                <p data-testid="auth-error" className="text-sm text-red-600">{authError}</p>
              )}
              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={!keyInput.trim()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Access Dashboard
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleRegionSelect = (region) => {
    setSelectedRegion(region === selectedRegion ? 'all' : region);
  };

  // Get current region data or aggregate
  const getCurrentStats = () => {
    if (!regionData?.regions) return null;
    
    if (selectedRegion === 'all') {
      return summary;
    }
    
    const regionStats = regionData.regions.find(r => r.region === selectedRegion);
    if (regionStats) {
      return {
        summary: regionStats.summary,
        dropoff: regionStats.dropoff,
        funnel: regionStats.funnel,
        top_webinars: regionStats.top_webinars
      };
    }
    return summary;
  };

  const getCurrentEvents = () => {
    if (selectedRegion === 'all') {
      return events;
    }
    
    const regionStats = regionData?.regions?.find(r => r.region === selectedRegion);
    return regionStats?.recent_events || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentStats = getCurrentStats();
  const currentEvents = getCurrentEvents();
  const stats = currentStats?.summary;
  const dropoff = currentStats?.dropoff;
  const funnel = currentStats?.funnel;
  const top_webinars = currentStats?.top_webinars;

  // Prepare region overview chart data
  const regionChartData = (regionData?.regions || [])
    .filter(r => r.summary?.total_plays > 0)
    .map(r => ({
      label: r.region,
      value: r.summary?.total_plays || 0,
      color: REGION_COLORS[r.region] || '#6B7280'
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
  
  const maxRegionPlays = Math.max(...regionChartData.map(d => d.value), 1);

  // Prepare top webinars chart data
  const topWebinarsChartData = (top_webinars || []).slice(0, 5).map(w => ({
    label: w.title?.substring(0, 30) + (w.title?.length > 30 ? '...' : ''),
    value: w.plays || 0
  }));
  
  const maxPlays = Math.max(...topWebinarsChartData.map(d => d.value), 1);

  // Drop-off chart data
  const dropoffChartData = [
    { label: 'Before 25%', value: dropoff?.at_25 || 0 },
    { label: '25% to 50%', value: dropoff?.at_50 || 0 },
    { label: '50% to 75%', value: dropoff?.at_75 || 0 },
    { label: '75% to Complete', value: dropoff?.before_complete || 0 }
  ];
  
  const maxDropoff = Math.max(...dropoffChartData.map(d => d.value), 1);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
            </div>
            <p className="text-gray-600">
              Track video engagement and viewer behavior across all webinars
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-[180px]">
                <Globe className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {(regionData?.available_regions || []).map(region => (
                  <SelectItem key={region} value={region}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: REGION_COLORS[region] || '#6B7280' }}
                      />
                      {region}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Region Filter Badge */}
        {selectedRegion !== 'all' && (
          <div className="mb-6 flex items-center gap-2">
            <Badge 
              className="px-3 py-1 text-sm"
              style={{ backgroundColor: REGION_COLORS[selectedRegion] || '#6B7280' }}
            >
              <MapPin className="h-3 w-3 mr-1" />
              {selectedRegion}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedRegion('all')}
              className="text-gray-500"
            >
              Clear filter
            </Button>
          </div>
        )}

        {/* Region Overview Cards */}
        {selectedRegion === 'all' && regionData?.regions && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              Regional Overview
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {regionData.regions
                .filter(r => r.summary?.total_plays > 0)
                .map(region => (
                  <RegionCard 
                    key={region.region}
                    region={region}
                    onClick={handleRegionSelect}
                    isSelected={selectedRegion === region.region}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Video Plays"
            value={stats?.total_plays?.toLocaleString() || '0'}
            subtitle={selectedRegion !== 'all' ? selectedRegion : 'All regions'}
            icon={Play}
            color="blue"
          />
          <MetricCard
            title="Total Completions"
            value={stats?.total_completions?.toLocaleString() || '0'}
            subtitle="Videos watched to end"
            icon={CheckCircle}
            color="green"
          />
          <MetricCard
            title="Completion Rate"
            value={`${stats?.completion_rate || 0}%`}
            subtitle="Of viewers who completed"
            icon={Target}
            color="purple"
          />
          <MetricCard
            title="Avg. Engagement"
            value={`${Math.round(((stats?.total_50_percent || 0) / (stats?.total_plays || 1)) * 100)}%`}
            subtitle="Reached 50% mark"
            icon={TrendingUp}
            color="orange"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Engagement Funnel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                Engagement Funnel
                {selectedRegion !== 'all' && (
                  <Badge variant="outline" className="ml-2 text-xs">{selectedRegion}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {funnel && funnel.length > 0 ? (
                <FunnelChart data={funnel} />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No funnel data available yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Regional Plays Distribution */}
          {selectedRegion === 'all' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-purple-600" />
                  Plays by Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                {regionChartData.length > 0 ? (
                  <SimpleBarChart 
                    data={regionChartData} 
                    maxValue={maxRegionPlays} 
                    color="purple" 
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No regional data available yet
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Drop-off Analysis - Show when region is selected */}
          {selectedRegion !== 'all' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                  Drop-off Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dropoffChartData.some(d => d.value > 0) ? (
                  <SimpleBarChart 
                    data={dropoffChartData} 
                    maxValue={maxDropoff} 
                    color="orange" 
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No drop-off data available yet
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-4">
                  Percentage of viewers who dropped off at each milestone
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Drop-off and Top Webinars Row - For all regions view */}
        {selectedRegion === 'all' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Drop-off Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                  Drop-off Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dropoffChartData.some(d => d.value > 0) ? (
                  <SimpleBarChart 
                    data={dropoffChartData} 
                    maxValue={maxDropoff} 
                    color="orange" 
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No drop-off data available yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Webinars by Plays */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Top Webinars by Plays
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topWebinarsChartData.length > 0 ? (
                  <SimpleBarChart 
                    data={topWebinarsChartData} 
                    maxValue={maxPlays} 
                    color="blue" 
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No webinar data available yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Webinars for selected region */}
        {selectedRegion !== 'all' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Top Webinars by Plays
                  <Badge variant="outline" className="ml-2 text-xs">{selectedRegion}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topWebinarsChartData.length > 0 ? (
                  <SimpleBarChart 
                    data={topWebinarsChartData} 
                    maxValue={maxPlays} 
                    color="blue" 
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No webinar data available for this region
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Webinars by Completion */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Top Webinars by Completion
                  <Badge variant="outline" className="ml-2 text-xs">{selectedRegion}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {top_webinars && top_webinars.length > 0 ? (
                  <div className="space-y-3">
                    {top_webinars.slice(0, 5).map((webinar, index) => (
                      <div 
                        key={webinar.webinar_id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-sm font-medium text-gray-400 w-6">
                            #{index + 1}
                          </span>
                          <span className="text-sm text-gray-900 truncate" title={webinar.title}>
                            {webinar.title?.substring(0, 25)}{webinar.title?.length > 25 ? '...' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {webinar.completions} completed
                          </Badge>
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            {webinar.completion_rate?.toFixed(0)}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No completion data for this region
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Events */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600" />
              Recent Events
              <Badge variant="outline" className="ml-2">
                {currentEvents.length} events
              </Badge>
              {selectedRegion !== 'all' && (
                <Badge 
                  className="ml-2 text-xs"
                  style={{ backgroundColor: REGION_COLORS[selectedRegion] || '#6B7280' }}
                >
                  {selectedRegion}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentEvents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Event Type</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Webinar ID</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Video Time</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Session</th>
                      {selectedRegion === 'all' && (
                        <th className="text-left py-3 px-2 font-medium text-gray-500">Region</th>
                      )}
                      <th className="text-left py-3 px-2 font-medium text-gray-500">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEvents.slice(0, 20).map((event, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <Badge 
                            variant="outline" 
                            className={
                              event.event_type === 'video_play' ? 'bg-blue-50 text-blue-700' :
                              event.event_type === 'video_complete' ? 'bg-green-50 text-green-700' :
                              event.event_type?.includes('progress') ? 'bg-orange-50 text-orange-700' :
                              'bg-gray-50 text-gray-700'
                            }
                          >
                            {event.event_type?.replace(/_/g, ' ') || 'unknown'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 font-mono text-xs text-gray-600">
                          {event.webinar_id?.substring(0, 10)}...
                        </td>
                        <td className="py-3 px-2 text-gray-600">
                          {event.video_time ? `${Math.floor(event.video_time / 60)}:${String(Math.round(event.video_time) % 60).padStart(2, '0')}` : '-'}
                        </td>
                        <td className="py-3 px-2 font-mono text-xs text-gray-500">
                          {event.session_id?.substring(0, 8)}...
                        </td>
                        {selectedRegion === 'all' && (
                          <td className="py-3 px-2">
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ 
                                borderColor: REGION_COLORS[event.region] || '#6B7280',
                                color: REGION_COLORS[event.region] || '#6B7280'
                              }}
                            >
                              {event.region || 'Unknown'}
                            </Badge>
                          </td>
                        )}
                        <td className="py-3 px-2 text-gray-500">
                          {event.timestamp ? new Date(event.timestamp).toLocaleString() : 
                           event.created_at ? new Date(event.created_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No events recorded yet{selectedRegion !== 'all' ? ` for ${selectedRegion}` : ''}.
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AnalyticsDashboard;
